<template>
<div class="blog-header">
    <div class="blog-header-content">
        <p v-t="'BLOG.TITLE1'" class="top-title" />
        <h1 v-t="'BLOG.TITLE2'" />
    </div>
</div>
<div v-if="tag" class="blog-filter"><h1>Tag: {{ tag?.tag }}</h1></div>
<div class="blog padded">
    <div class="search-field">
        <span class="icon">
            <i class="fa-regular fa-search" />
        </span>
        <input
            id="searchInput"
            v-model="searchQuery"
            :placeholder="$t('FORM.SEARCH')"
            class="input"
            inputmode="text"
            aria-label="$t('FORM.SEARCH')"
            v-on:keydown.enter="submitSearch"
        >
        <ButtonElement
            id="submitButton"
            class="primary submit-btn small"
            v-on:click="submitSearch"
            v-on:keydown.enter="submitSearch"
        >
            {{ $t('FORM.SEARCH') }}
        </ButtonElement>
        <NuxtLink :to="localeRoute('/blog/year')" class="anchor-button small">
            <i class="fa-regular fa-calendar" />
        </NuxtLink>
    </div>

    <div class="blog-posts">
        <BlogTile v-for="post in posts" :key="post.slug" :post="post" />
    </div>
    <div class="load-more">
        <button class="wide outline" v-on:click="loadMore">{{ $t('BLOG.LOAD_MORE') }}</button>
    </div>
</div>
</template>

<script setup lang="ts">

import ButtonElement from '$components/ButtonElement.vue'
import BlogTile from '$components/BlogPostTile.vue'
import type { CMS } from '$plugins/cms'
import { JBGWeb } from '$types/JBGWeb'

const route = useRoute()
const i18n = useI18n()
const app = useNuxtApp()
const localeRoute = useLocaleRoute()

let isLoading = false
let tag: JBGWeb.Tag
let searchQuery = ''
let page = 1
let queryTag = (Array.isArray(route.query.tag) ? route.query.tag[0] : route.query.tag) || ''
const pageSize = 20

const { data: posts } = useAsyncData<JBGWeb.BlogPost[]>(
    `posts-${searchQuery}-${page}-${queryTag}-${i18n.locale.value}`,
    async () => {
        try {
            isLoading = true
            return await loadBlogs()
        } catch (error) {
            console.error('error loading blog posts')
            throw error
        } finally {
            isLoading = false
        }
    }
)

// eslint-disable-next-line complexity
async function loadBlogs() {
    try {
        const options: (CMS.FetchOptions & { withMeta?: false | undefined; }) = {
            fields: ['slug', 'title', 'featureRank', 'publishedAt'],
            populate: ['featuredImage'],
            sort: 'publishedAt:desc',
            pagination: {
                page,
                pageSize
            },
            locale: i18n.locale.value
        }

        queryTag = Array.isArray(route.query.tag) ? route.query.tag[0] : route.query.tag
        if (queryTag && queryTag.length > 0) {
            tag = await app.$cms.fetchOne<JBGWeb.Tag>('tags', queryTag, {
                ids: true,
                locale: i18n.locale.value
            })
            if (tag) {
                options.filters = { tags: tag.id }
            }
        }

        searchQuery = Array.isArray(route.query.s) ? route.query.s[0] : route.query.s
        if (searchQuery && searchQuery.length > 0) {
            options.filters = {
                '$or][0][content][$containsi': searchQuery,
                '$or][1][title][$containsi': searchQuery
            }
        }
        const blogs = await app.$cms.fetchMany<JBGWeb.BlogPost[]>('blog-posts', options)
        if (!posts.value) {
            posts.value = blogs
        } else if (Array.isArray(blogs)) {
            posts.value.push(...blogs)
        } else {
            return []
        }

        return blogs
    } catch (error) {
        console.error(`Failed to fetch blog page ${searchQuery}:`, error.message)
        throw createError({ statusCode: 500, statusMessage: 'error loading blog posts' })
    } finally {
        isLoading = false
    }
}

async function loadMore() {
    if (isLoading) {
        return
    }
    page += 1
    await loadBlogs()
}

async function submitSearch() {
    page = 1
    posts.value = []
    const queries = { ...route.query }
    queries.s = searchQuery
    await app.$router.replace({ query: queries })
    await loadBlogs()
}

useHead({
    title: `${i18n.t('COMPANY.BLOG')}`,
    meta: [
        { name: 'og:title', content: `${i18n.t('COMPANY.BLOG')}` },
        { name: 'description', content: `${i18n.t('META.BLOG')}` }
    ],
    link: [{
        rel: 'canonical',
        href: queryTag ? `https://www.jackboxgames.com/blog?tag=${queryTag}` : 'https://www.jackboxgames.com/blog'
    }]
})

</script>

<style lang="scss">
@use "$styles/kit.scss" as *;

$overlayGradient: linear-gradient(0deg,
    rgba(0,0,0,0) 30%,
    rgba(0,0,0,.75) 100%);

.blog-header {
    background-image: url("/images/blog-header.jpg");
    background-size: cover;
    background-position: center;
    height: 400px;
    margin-top: -88px;
    margin-bottom: 30px;
    padding-top: 100px;
    display: flex;
    @include mq-small-and-less {
        height: 300px;
    }

    &::after { // overlay
        content: '';
        z-index: 1;
        position: absolute;
        top: 0;
        left: 50%;
        width: 100%;
        height: 400px;
        background: $overlayGradient;
        transform: translateX(-50%);
        @include mq-small-and-less {
            height: 300px;
        }

    }

    .blog-header-content {
        display: inline-block;
        z-index: 2;
        text-align: center;
        width: 100%;
        margin-top: auto;
        margin-bottom: auto;
        padding-bottom: 50px;
        text-shadow: 0px 0px 10px #000000a0;
        h1, .top-title {
            text-transform: uppercase;
            margin: 0;
        }
        h1 {
            font-size: 72px;
            margin: 0;
        }
        .top-title {
            font-size: 40px;
            font-weight: bold;
        }
        @include mq-small-and-less {
            h1 { font-size: 40px; }
            .top-title { font-size: 25px; }
        }
    }
}

.load-more {
    margin: auto;
    max-width: 900px;
    width: 100%;
    padding-bottom: 20px;
}

.blog-filter {
    max-width: 900px;
    margin: auto;
}

.blog-posts {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    max-width: 900px;
    margin: auto;
    padding-bottom: 20px;

    .blog-post-link:first-child {
        grid-column-start:1;
        grid-column-end: 3;
        padding-left: 50px;
        padding-right: 50px;
        .blog-title {
            // font-size: 30px;
            line-height: 30px;
        }
    }
}

.search-field {
    max-width: 900px;
    margin: auto;
    display: flex;
    gap: 0px;
    padding-bottom: 15px;
    .input {
        padding-left: 36px;
        z-index: 0;
        margin-right: 10px;
    }
    .icon {
        position: relative;
        top: 10px;
        left: 8px;
        z-index: 1;
        width: 0px;
    }
}
</style>
